var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var FilterStatement_1;
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchema
import { FilterStatementOp as FSO } from '@symon-ai/wisepipe-base-pipe';
import { DateConverter, JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { Filter, FilterType } from './filter';
import { FilterLiteralType } from './filterLiteral';
import './filterOperand'; // force import for JsonSchema
import { FilterOperandType } from './filterOperand';
export var FilterStatementOp = FSO;
const schema = {
    $id: 'urn:wisepipe-common-pipe:FilterStatement',
    type: 'object',
    properties: {
        lhs: { $ref: 'urn:wisepipe-common-pipe:FilterOperand' },
        op: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                {
                    type: 'string',
                    enum: Object.values(FilterStatementOp)
                }
            ]
        },
        rhs: { $ref: 'urn:wisepipe-common-pipe:FilterOperand' }
    },
    required: ['lhs', 'op'],
    if: {
        properties: {
            op: {
                type: 'string',
                enum: Object.values(FilterStatementOp).filter((key) => key !== FilterStatementOp.IS_NULL && key !== FilterStatementOp.IS_NOT_NULL)
            }
        }
    },
    then: {
        required: ['rhs']
    }
};
export var FilterStatementErrors;
(function (FilterStatementErrors) {
    FilterStatementErrors["LITERAL_TO_NULL"] = "LITERAL_TO_NULL";
    FilterStatementErrors["LITERAL_TO_LITERAL"] = "LITERAL_TO_LITERAL";
    FilterStatementErrors["LHS_TO_NULL_HAS_RHS"] = "LHS_TO_NULL_HAS_RHS";
    FilterStatementErrors["NO_METADATA"] = "NO_METADATA";
    FilterStatementErrors["LHS_MISMATCH_METADATA"] = "LHS_MISMATCH_METADATA";
    FilterStatementErrors["RHS_MISMATCH_METADATA"] = "RHS_MISMATCH_METADATA";
    FilterStatementErrors["LHS_MISMATCH_RHS"] = "LHS_MISMATCH_RHS";
    FilterStatementErrors["LHS_REQUIRED"] = "LHS_REQUIRED";
    FilterStatementErrors["RHS_REQUIRED"] = "RHS_REQUIRED";
    FilterStatementErrors["LHS_MUST_BE_NUMERIC"] = "LHS_MUST_BE_NUMERIC";
    FilterStatementErrors["RHS_MUST_BE_NUMERIC"] = "RHS_MUST_BE_NUMERIC";
    FilterStatementErrors["LHS_MUST_BE_BOOLEAN"] = "LHS_MUST_BE_BOOLEAN";
    FilterStatementErrors["RHS_MUST_BE_BOOLEAN"] = "RHS_MUST_BE_BOOLEAN";
    FilterStatementErrors["LHS_MUST_BE_STRING"] = "LHS_MUST_BE_STRING";
    FilterStatementErrors["RHS_MUST_BE_STRING"] = "RHS_MUST_BE_STRING";
    FilterStatementErrors["LHS_BOOLEAN_WRONG_OP"] = "LHS_BOOLEAN_WRONG_OP";
    FilterStatementErrors["RHS_BOOLEAN_WRONG_OP"] = "RHS_BOOLEAN_WRONG_OP";
    FilterStatementErrors["LHS_MUST_BE_DATE"] = "LHS_MUST_BE_DATE";
    FilterStatementErrors["RHS_MUST_BE_DATE"] = "RHS_MUST_BE_DATE";
    FilterStatementErrors["MISMATCH_PERIOD_METADATA"] = "MISMATCH_PERIOD_METADATA"; // lhs and rhs period metadata must be same if both are period columns
})(FilterStatementErrors || (FilterStatementErrors = {}));
let FilterStatement = FilterStatement_1 = class FilterStatement extends Filter {
    constructor(lhs, op, rhs) {
        super(FilterType.STATEMENT);
        this.lhs = lhs;
        this.op = op;
        this.rhs = rhs;
        JSON.validate(this, FilterStatement_1);
    }
    get opFormula() {
        switch (this.op) {
            case FilterStatementOp.LESS_THAN:
                return '<';
            case FilterStatementOp.LESS_THAN_EQUALS:
                return '<=';
            case FilterStatementOp.EQUALS:
                return '==';
            case FilterStatementOp.NOT_EQUALS:
                return '!= ';
            case FilterStatementOp.GREATER_THAN:
                return '>';
            case FilterStatementOp.GREATER_THAN_EQUALS:
                return '>=';
            case FilterStatementOp.IS_NULL:
                return 'Isnull';
            case FilterStatementOp.IS_NOT_NULL:
                return 'not Isnull';
            case FilterStatementOp.STARTS_WITH:
                return 'Startswith';
            case FilterStatementOp.ENDS_WITH:
                return 'Endswith';
            case FilterStatementOp.CONTAINS:
                return 'Contains';
            case FilterStatementOp.NOT_CONTAINS:
                return 'not Contains';
            default:
                return '';
        }
    }
    // for using filter inside case tool, used in codeGenerator
    get formula() {
        if (this.op === FilterStatementOp.IS_NULL || this.op === FilterStatementOp.IS_NOT_NULL) {
            return `${this.opFormula}(${this.lhs.formulaString})`;
        }
        else if (this.op === FilterStatementOp.STARTS_WITH ||
            this.op === FilterStatementOp.ENDS_WITH ||
            this.op === FilterStatementOp.CONTAINS ||
            this.op === FilterStatementOp.NOT_CONTAINS) {
            return `${this.opFormula}(${this.lhs.formulaString}, ${this.rhs.formulaString})`;
        }
        return `${this.lhs.formulaString} ${this.opFormula} ${this.rhs.formulaString}`;
    }
    static validateCols(errors, col, lit, op, isColLeft) {
        // parent method already applied params, so just cast as string
        const val = lit ? lit.value : undefined;
        const litType = lit ? lit.litType : undefined;
        if (val === undefined || val === null || val === '') {
            errors.push(isColLeft ? FilterStatementErrors.RHS_REQUIRED : FilterStatementErrors.LHS_REQUIRED);
        }
        else if (col.type === 'string' || col.type === 'period') {
            if (litType !== FilterLiteralType.STRING) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_STRING : FilterStatementErrors.LHS_MUST_BE_STRING);
            }
        }
        else if (col.type === 'number') {
            if (litType !== FilterLiteralType.NUMBER || isNaN(val)) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_NUMERIC : FilterStatementErrors.LHS_MUST_BE_NUMERIC);
            }
            if (op === FilterStatementOp.STARTS_WITH ||
                op === FilterStatementOp.ENDS_WITH ||
                op === FilterStatementOp.CONTAINS ||
                op === FilterStatementOp.NOT_CONTAINS) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_STRING : FilterStatementErrors.LHS_MUST_BE_STRING);
            }
        }
        else if (col.type === 'boolean') {
            if (litType !== FilterLiteralType.BOOLEAN || (val.toLowerCase() !== 'true' && val.toLowerCase() !== 'false')) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_BOOLEAN : FilterStatementErrors.LHS_MUST_BE_BOOLEAN);
            }
            if (op === FilterStatementOp.GREATER_THAN ||
                op === FilterStatementOp.GREATER_THAN_EQUALS ||
                op === FilterStatementOp.LESS_THAN ||
                op === FilterStatementOp.LESS_THAN_EQUALS) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_BOOLEAN_WRONG_OP : FilterStatementErrors.LHS_BOOLEAN_WRONG_OP);
            }
            else if (op === FilterStatementOp.STARTS_WITH ||
                op === FilterStatementOp.ENDS_WITH ||
                op === FilterStatementOp.CONTAINS ||
                op === FilterStatementOp.NOT_CONTAINS) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_STRING : FilterStatementErrors.LHS_MUST_BE_STRING);
            }
        }
        else if (col.type === 'date') {
            if (litType !== FilterLiteralType.DATE || !DateConverter.isDate(val) || !/^\d*-\d*-\d*([TZ\d:.]*)?$/.test(val)) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_DATE : FilterStatementErrors.LHS_MUST_BE_DATE);
            }
            if (op === FilterStatementOp.STARTS_WITH ||
                op === FilterStatementOp.ENDS_WITH ||
                op === FilterStatementOp.CONTAINS ||
                op === FilterStatementOp.NOT_CONTAINS) {
                errors.push(isColLeft ? FilterStatementErrors.RHS_MUST_BE_STRING : FilterStatementErrors.LHS_MUST_BE_STRING);
            }
        }
    }
    validate(parameters, leftSideSource, rightSideSource) {
        const errors = [];
        const { rhs, lhs } = this.applyParameters(parameters);
        const op = parameters.getEnumVal(this.op);
        if (op === FilterStatementOp.IS_NULL || op === FilterStatementOp.IS_NOT_NULL) {
            if (lhs.operandType === FilterOperandType.LITERAL) {
                errors.push(FilterStatementErrors.LITERAL_TO_NULL);
            }
            if (leftSideSource && leftSideSource.length !== 0) {
                const leftCol = leftSideSource.find((col) => col.name === lhs.name);
                if (!leftCol) {
                    errors.push(FilterStatementErrors.LHS_MISMATCH_METADATA);
                }
            }
            if (rhs) {
                errors.push(FilterStatementErrors.LHS_TO_NULL_HAS_RHS);
            }
            return errors;
        }
        if (lhs.operandType === FilterOperandType.LITERAL && rhs && rhs.operandType === FilterOperandType.LITERAL) {
            errors.push(FilterStatementErrors.LITERAL_TO_LITERAL);
        }
        if (!leftSideSource || leftSideSource.length === 0) {
            errors.push(FilterStatementErrors.NO_METADATA);
        }
        else {
            let leftCol;
            let rightCol;
            if (lhs.operandType === FilterOperandType.COLUMN) {
                leftCol = leftSideSource.find((col) => col.name === lhs.name);
                if (!leftCol) {
                    errors.push(FilterStatementErrors.LHS_MISMATCH_METADATA);
                }
                else if (!['string', 'period'].includes(leftCol.type) &&
                    (op === FilterStatementOp.STARTS_WITH ||
                        op === FilterStatementOp.ENDS_WITH ||
                        op === FilterStatementOp.CONTAINS ||
                        op === FilterStatementOp.NOT_CONTAINS)) {
                    errors.push(FilterStatementErrors.LHS_MUST_BE_STRING);
                }
            }
            if (rhs && rhs.operandType === FilterOperandType.COLUMN) {
                rightCol = (rightSideSource ?? leftSideSource).find((col) => col.name === rhs.name);
                if (!rightCol) {
                    errors.push(FilterStatementErrors.RHS_MISMATCH_METADATA);
                }
                else if (rightCol.type !== 'string' &&
                    (op === FilterStatementOp.STARTS_WITH ||
                        op === FilterStatementOp.ENDS_WITH ||
                        op === FilterStatementOp.CONTAINS ||
                        op === FilterStatementOp.NOT_CONTAINS)) {
                    errors.push(FilterStatementErrors.RHS_MUST_BE_STRING);
                }
            }
            if (leftCol && rightCol && leftCol.type !== rightCol.type) {
                errors.push(FilterStatementErrors.LHS_MISMATCH_RHS);
            }
            // if left and right col are period columns, check if period metadata is same
            if (leftCol && rightCol && leftCol.type === 'period' && rightCol.type === 'period') {
                if (leftCol.metadata?.calendarID !== rightCol.metadata?.calendarID ||
                    leftCol.metadata?.level !== rightCol.metadata?.level) {
                    errors.push(FilterStatementErrors.MISMATCH_PERIOD_METADATA);
                }
            }
            if (errors.length > 0)
                return errors;
            if (leftCol && !rightCol) {
                const rLit = rhs ? rhs : undefined;
                FilterStatement_1.validateCols(errors, leftCol, rLit, op, true);
            }
            if (rightCol && !leftCol) {
                const lLit = lhs ? lhs : undefined;
                FilterStatement_1.validateCols(errors, rightCol, lLit, op, false);
            }
        }
        return errors;
    }
    isValid(parameters, source) {
        return this.validate(parameters, source).length === 0;
    }
    getColumnArguments(parameters) {
        const colArgs = new Set();
        const { rhs, lhs } = this;
        if (lhs.operandType === FilterOperandType.COLUMN) {
            colArgs.add(lhs.getBaseValues(parameters).name);
        }
        if (rhs && rhs.operandType === FilterOperandType.COLUMN) {
            colArgs.add(rhs.getBaseValues(parameters).name);
        }
        return colArgs;
    }
    applyParameters(parameters) {
        return new FilterStatement_1(this.lhs.applyParameters(parameters), parameters.getEnumVal(this.op), this.rhs?.applyParameters(parameters));
    }
    getBaseValues(parameters) {
        return {
            lhs: this.lhs.getBaseValues(parameters),
            rhs: this.rhs?.getBaseValues(parameters),
            op: parameters.getEnumVal(this.op)
        };
    }
};
FilterStatement = FilterStatement_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(FilterType.STATEMENT)
], FilterStatement);
export { FilterStatement };
//# sourceMappingURL=filterStatement.js.map